import { FC, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import LogoImg from "../../../assets/logo/logo.svg";
import { Avatar, Button, InputType } from "../../../components/common";
import { IntercomObserver } from "../../../components/observers";
import { TransferCalculator } from "../../../components/widgets";
import { CURRENCY_MAP, DEFAULT_FIAT_CURRENCY } from "../../../constants";
import {
  selectPaymentRequestLinks,
  selectSelectedCalculatorInput,
  setCalculatorInput,
  nextStep,
} from "../../../store/reducers/paymentRequestLink";
import { Currency } from "../../../types/InvoiceTypes";
import { PublicPaymentRequest } from "../../../types/PaymentRequestTypes";
import { useLocalStorage } from "../../../utils/hooks/useLocalStorage";
import { formatAmount } from "../../../utils/numbers/money";

export const TransferAmounts: FC = () => {
  const actionContainerRef = useRef<HTMLDivElement | null>(null);
  const paymentRequestLink = useSelector(selectPaymentRequestLinks);
  const dispatch = useDispatch();
  const selectedCalculatorInput = useSelector(selectSelectedCalculatorInput);
  const selectedCurrency =
    selectedCalculatorInput?.currency || DEFAULT_FIAT_CURRENCY;

  const { data: publicPaymentRequest, remove: removeSessionStorage } =
    useLocalStorage<PublicPaymentRequest>("publicPaymentRequest");

  useEffect(() => {
    if (
      paymentRequestLink &&
      publicPaymentRequest &&
      publicPaymentRequest.payment_request_link !== paymentRequestLink.id
    ) {
      removeSessionStorage();
    }
  }, [paymentRequestLink, publicPaymentRequest, removeSessionStorage]);

  useEffect(() => {
    dispatch(setCalculatorInput({ currency: Currency.EUR, amount: 100 }));
  }, [dispatch]);

  if (!paymentRequestLink) {
    return null;
  }

  const { beneficiary_name, usd_incoming_enabled } = paymentRequestLink;

  const onChangeCalculator = (input: Partial<InputType>) => {
    if (input.currency) {
      dispatch(setCalculatorInput({ ...selectedCalculatorInput, ...input }));
    }
  };

  const onClickContinue = () => dispatch(nextStep());

  const maxAmountProp = `max_${selectedCurrency}_amount_threshold`;
  const maxAmount = (paymentRequestLink as any)[maxAmountProp] as number;
  const minAmount = (paymentRequestLink as any)[
    "min_amount_threshold"
  ] as number;

  const allowedIncomingCurrencies = usd_incoming_enabled
    ? [Currency.USD, Currency.EUR, Currency.GBP]
    : [Currency.EUR, Currency.GBP];

  return (
    <div className="flex flex-col w-full md:items-center">
      <div className="md:w-1/2 flex flex-col gap-2">
        <div className="flex flex-col px-4 gap-4">
          <div className="flex pt-2">
            <img src={LogoImg} alt="Logo" className="w-[124px]" />
          </div>

          <div>
            <div className="flex justify-between pt-2">
              <h4 className="font-poppins font-bold text-2xl">
                Transfer to {beneficiary_name}
              </h4>

              <div>
                <Avatar title={beneficiary_name} />
              </div>
            </div>

            <div>How much do you want to send?</div>
          </div>

          <TransferCalculator
            allowedIncomingCurrencies={allowedIncomingCurrencies}
            allowedOutgoingCurrencies={[Currency.INR]}
            maxIncomingAmount={maxAmount}
            priceData={paymentRequestLink}
            onChange={onChangeCalculator}
          />
        </div>

        <div className="flex flex-col gap-4 p-4 bg-white w-full !z-[25]">
          <div className="flex bg-brown-50 p-4 rounded-2xl">
            <div className="basis-1/2 flex flex-col gap-2">
              <div className="font-poppins text-base font-bold">
                {CURRENCY_MAP[selectedCurrency].symbol}{" "}
                {formatAmount(maxAmount, 0)}
              </div>

              <div className="text-sm text-violet-300 font-semibold">
                Max amount
              </div>
            </div>

            <div className="basis-1/2 flex flex-col gap-2">
              <div className="font-poppins text-base font-bold">
                {CURRENCY_MAP[selectedCurrency].symbol}{" "}
                {formatAmount(minAmount, 0)}
              </div>

              <div className="text-sm text-violet-300 font-semibold">
                Min amount
              </div>
            </div>
          </div>

          <div ref={actionContainerRef}>
            <Button
              block
              variant={Button.variant.PRIMARY}
              onClick={onClickContinue}
            >
              Continue
            </Button>
          </div>

          <IntercomObserver footer={actionContainerRef} />
        </div>
      </div>
    </div>
  );
};
